import {
  FormControlLabel,
  Switch,
  TextField,
  Grid,
  Button,
  FormHelperText,
  IconButton,
  Box
} from '@mui/material';

import {useCallback, useMemo, useState} from 'react';

import PhotoCamera from '@mui/icons-material/PhotoCamera';

import {Formik, Form} from 'formik';

import GroupValidationSchema from '@/schemas/groups';

import {useTranslation} from 'react-i18next';

import {useDispatch} from 'react-redux';
import {
  setGroupFormData,
  resetGroupFormData,
  initialState,
  GroupCreateFormState
} from '@/redux-store/slices/groups/';

import {TITLE_MAX_LENGTH, DESC_MAX_LENGTH} from '@/schemas/groups';
import {useAppSelector} from '@/redux-store/store';

import {createGroup} from '@/rest/groups/create-group';
import {LocationInput} from '@/components/forms/input/location';
import {Location} from '@/schemas/sub-schemas';
import {SportSelectInput} from '@/components/forms/input/sports';
import {GroupFormData} from '@/types/group';
import {DEFAULT_LATITUDE, DEFAULT_LONGITUDE} from '@/constants';
import CachedIcon from '@mui/icons-material/Cached';
import {env} from '@/config';
import TypographyTitle from '@/components/ui/typography-title';
import {CompanyInput} from '@/components/forms/input/company';
import {decodeBase64ToObject, getAppTag, isAllowedTag} from '@/utils';
import ResponsiveTypography from '@/components/ui/responsive-typography';

const {CONTENT_SERVER} = env;
const groupsImage = `${CONTENT_SERVER}assets/sports/team-new.jpg`;

interface GroupCreateFormProps {
  onCancel?: () => void;
  onSubmit?: (newTeam: GroupFormData) => void;
  onError?: (error: Error) => void;
}

// TODO: Refactor this! We only need one component for creation and edition!

const GroupCreateForm: React.FC<GroupCreateFormProps> = ({
  onCancel,
  onSubmit,
  onError
}) => {
  const {APP_VARIANT, TAG_PAIRS} = env;
  const {t} = useTranslation();

  const initialValues: GroupCreateFormState = useAppSelector(
    (state) => state.groups.groupCreateForm
  );
  const userValues = useAppSelector((state) => state.userInfos.userInfos);
  const sportsFromRedux = useAppSelector((state) => state.sports);
  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);
  const availableCompanies = userValues.companies;
  const tagPairs = decodeBase64ToObject(TAG_PAIRS);

  const companyId =
    APP_VARIANT === 'sjone' &&
    userValues &&
    userValues.companies &&
    userValues.companies.length === 1 &&
    userValues.companies[0].id._id;

  const dispatch = useDispatch();

  const trainerInSjOne = userRole === 'TRAINER' && APP_VARIANT === 'sjone';

  const groupCreateSchema = GroupValidationSchema(t, trainerInSjOne);

  const [titleLength, setTitleLength] = useState(initialValues.title.length);
  const [descriptionLength, setDescriptionLength] = useState(
    initialValues.description.length
  );
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  const tag = getAppTag(userValues.email || '', tagPairs);
  const allowedTag = tag && isAllowedTag(tag, TAG_PAIRS) ? tag : undefined;

  const resetTextLength = useCallback(() => {
    setTitleLength(0);
    setDescriptionLength(0);
  }, []);

  const handleLocationSelected = useCallback(
    (location: Location) => {
      dispatch(setGroupFormData({location}));
    },
    [dispatch]
  );

  const resetFormData = useCallback(() => {
    dispatch(resetGroupFormData());
    resetTextLength();

    setFileSizeError(null);
  }, [dispatch, resetTextLength]);

  const handleCreateGroupSubmit = useCallback(
    (values: GroupCreateFormState) => {
      const {location, title, description, sports, accessType, company} =
        values;
      if (location) {
        const groupObject = {
          createdBy: {_id: userValues._id},
          managers: [userValues._id],
          title,
          description,
          locationName: location.label,
          location: {
            coordinates: [
              location.lng || DEFAULT_LONGITUDE,
              location.lat || DEFAULT_LATITUDE
            ]
          },
          picture: initialValues.picture,
          sports,
          accessType: accessType,
          company: companyId || company || undefined,
          trainerGroup: userRole === 'TRAINER' ? true : false,
          tag: allowedTag
        };

        createGroup(groupObject, userValues._id)
          .then((response) => {
            if (response) {
              if (onSubmit) {
                onSubmit(response);
                resetFormData();
              }
            } else {
              if (onError) {
                onError(new Error('Error creating new team'));
              }
            }
          })
          .catch((error) => {
            console.error('Error:', error);
            if (onError) {
              onError(error as Error);
            }
          });
      }
    },
    [
      initialValues.picture,
      onError,
      onSubmit,
      resetFormData,
      userValues,
      companyId,
      userRole,
      allowedTag
    ]
  );

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const fileSize = file.size / 1024 / 1024;
      if (fileSize > 0.5) {
        setFileSizeError(t('file_size_error', {maxSize: 500, unit: 'KB'}));

        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(event.target.files[0]);
      reader.onload = () => {
        dispatch(
          setGroupFormData({
            picture: reader.result as string
          })
        );
        setFileSizeError(null);
      };
    }
  };

  const pictureSource = useMemo(() => {
    return initialValues.picture
      ? initialValues.picture
      : initialValues.sports?.length === 1
      ? sportsFromRedux.sports[
          initialValues.sports ? initialValues.sports[0] : 0
        ]?.picture
      : groupsImage || null;
  }, [initialValues.picture, sportsFromRedux.sports, initialValues.sports]);

  const handleDeleteImage = useCallback(() => {
    dispatch(
      setGroupFormData({
        picture: null
      })
    );
    setFileSizeError(null);
  }, [dispatch]);

  return (
    <>
      <Formik
        //enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={groupCreateSchema}
        onSubmit={(values) => {
          handleCreateGroupSubmit(values);
        }}
        validateOnBlur={true}
        validateOnChange={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          resetForm,
          setFieldValue,
          setFieldTouched
        }) => {
          // console.log(errors);
          return (
            <Form>
              <TypographyTitle textKey="new_group" />
              {trainerInSjOne &&
                availableCompanies &&
                availableCompanies.length > 0 && (
                  <Grid container spacing={2} marginTop={1} marginBottom={3}>
                    <Grid item xs={12}>
                      <CompanyInput
                        companies={availableCompanies}
                        value={
                          availableCompanies.find(
                            (company) => company.id._id === values.company
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          if (newValue && !Array.isArray(newValue)) {
                            setFieldValue('company', newValue.id._id);
                          } else {
                            setFieldValue('company', undefined);
                          }
                        }}
                        error={errors.company}
                        touched={!!touched.company}
                        onBlur={handleBlur}
                        label={t('company')}
                      />
                    </Grid>
                  </Grid>
                )}

              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <TextField
                    name="title"
                    label={t('event_prop_title')}
                    value={values.title}
                    onChange={(e) => {
                      handleChange(e);
                      setTitleLength(e.target.value.length);
                      dispatch(setGroupFormData({title: e.target.value}));
                    }}
                    onBlur={handleBlur}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                    fullWidth
                  />
                  <FormHelperText>
                    {titleLength} / {TITLE_MAX_LENGTH}{' '}
                  </FormHelperText>
                </Grid>
              </Grid>
              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    label={t('description')}
                    value={values.description}
                    onChange={(e) => {
                      handleChange(e);
                      setDescriptionLength(e.target.value.length);
                      dispatch(
                        setGroupFormData({
                          description: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    fullWidth
                    multiline
                    maxRows={4}
                  />

                  <FormHelperText>
                    {descriptionLength} / {DESC_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>
              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <LocationInput
                    name="location"
                    label={t('location')}
                    value={values.location}
                    error={errors['location']}
                    touched={touched['location']}
                    onBlur={() => {
                      setFieldTouched('location', true);
                    }}
                    onChange={(location) => {
                      setFieldValue('location', location);
                      if (location) {
                        handleLocationSelected(location);
                      }
                    }}
                    initialLocation={
                      initialValues.location || userValues?.location
                    }
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} my={1} marginTop={3}>
                <Grid item xs={12}>
                  <SportSelectInput
                    name="sports"
                    optional
                    value={values.sports}
                    error={errors.sports}
                    touched={touched.sports}
                    onBlur={handleBlur}
                    onChange={(sportIds) => {
                      setFieldValue('sports', sportIds);
                      dispatch(
                        setGroupFormData({
                          sports: sportIds
                        })
                      );
                    }}
                    multiple={true}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} my={3} justifyContent={'center'}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{display: 'flex', justifyContent: 'center'}}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.accessType === 'PRIVATE'}
                        onChange={(e) => {
                          const newValue = e.target.checked
                            ? 'PRIVATE'
                            : 'PUBLIC';
                          setFieldValue('accessType', newValue);
                          dispatch(
                            setGroupFormData({
                              accessType: newValue
                            })
                          );
                        }}
                        name="accessType"
                      />
                    }
                    label={t('group_private')}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                spacing={2}
                marginBottom={3}
                marginTop={{xs: 0, sm: 3}}
                justifyContent={'center'}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{display: 'flex', justifyContent: 'center'}}
                >
                  <Button
                    component="span"
                    variant="outlined"
                    color="error"
                    startIcon={<CachedIcon />}
                    onClick={handleDeleteImage}
                    sx={{
                      display: {xs: 'none', sm: 'flex'},
                      textTransform: 'none'
                    }}
                  >
                    {t('show_default_photo')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{display: 'flex', justifyContent: 'center'}}
                >
                  <label htmlFor="photo-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      color="secondary"
                      startIcon={<PhotoCamera />}
                      sx={{
                        display: {xs: 'none', sm: 'flex'},
                        textTransform: 'none'
                      }}
                    >
                      {t('upload_photo')}
                    </Button>
                  </label>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginRight: 2,
                    marginBottom: 1
                  }}
                >
                  <label htmlFor="phote-delete-xs">
                    <IconButton
                      aria-label={t('show_default_photo')}
                      color="error"
                      onClick={handleDeleteImage}
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'}
                      }}
                    >
                      <CachedIcon />
                    </IconButton>
                  </label>

                  <input
                    id="photo-upload"
                    type="file"
                    onChange={handleImageChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />

                  <label htmlFor="photo-upload-xs">
                    <IconButton
                      color="secondary"
                      aria-label={t('upload_photo')}
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'},
                        marginLeft: 4,
                        marginTop: '1px'
                      }}
                    >
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  <input
                    id="photo-upload-xs"
                    type="file"
                    onChange={handleImageChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                </Grid>
              </Grid>

              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  marginTop: {xs: 0, sm: 1}
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 2,
                    flexDirection: 'column'
                  }}
                >
                  <ResponsiveTypography>
                    {t('group_photo')}
                  </ResponsiveTypography>
                  <ResponsiveTypography>
                    {t('optimal_dimensions', {width: 2, height: 1})}
                  </ResponsiveTypography>
                </Box>
                <Box
                  component="img"
                  src={pictureSource as string}
                  alt={t('group_image')}
                  sx={{
                    // marginRight: '10px',
                    objectFit: 'cover',
                    width: {xs: '240px', sm: '300px'},
                    height: {xs: '120px', sm: '150px'},

                    // maxWidth: { xs: '240px', sm: '300px' },
                    // maxHeight: { xs: '120px', sm: '150px' },
                    marginTop: {xs: 2, sm: 0}
                  }}
                />
              </Grid>

              {fileSizeError && (
                <ResponsiveTypography
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    color: 'red',
                    marginTop: 3
                  }}
                >
                  {fileSizeError}
                </ResponsiveTypography>
              )}

              <Grid
                container
                spacing={2}
                my={1}
                marginTop={2}
                marginBottom={{xs: 0, sm: 4}}
              >
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: {xs: 3, sm: 0},
                    order: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      resetFormData();
                      resetForm({
                        values: initialState.groupCreateForm
                      });
                      if (onCancel) {
                        onCancel();
                      }
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('cancel')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      resetFormData();
                      resetForm({
                        values: initialState.groupCreateForm
                      });
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('reset')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: {xs: 1, sm: 0},
                    order: {xs: -1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!!fileSizeError}
                    onClick={() => {
                      handleSubmit();
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('create')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default GroupCreateForm;
